<template>
    <ContainerContent
        class="mb-16"
        max-class="max-w-sm-ceil"
        outer-container
    >
        <div
            class="my-4"
        >
            <h1 class="page-title">Cookie Policy</h1>
            <i>Last Updated: June 2026</i>
        </div>

        <p>
            This Cookie Policy explains how {{ brandTitle }} ("we," "us," or "our") uses
            cookies and similar technologies on our website.
        </p>

        <h2>What Are Cookies?</h2>

        <p>
            Cookies are small text files stored on your device when you visit a website.
            They help websites function properly, improve security, remember information
            during your visit, and provide insights into website usage.
        </p>

        <p>
            In addition to cookies, we may use similar technologies such as pixels, local
            storage, and scripts.
        </p>

        <h2>Types of Cookies We Use</h2>

        <h3>Necessary Cookies</h3>

        <p>
            These cookies are required for the operation of our website and cannot be
            disabled through our cookie preference tools.
        </p>

        <p>Necessary cookies may be used to:</p>

        <ul>
            <li>Authenticate users</li>
            <li>Maintain secure sessions</li>
            <li>Protect against fraud and abuse</li>
            <li>Remember consent preferences</li>
            <li>Enable core website functionality</li>
        </ul>

        <p>
            Without these cookies, some portions of the website may not function properly.
        </p>

        <h3>Analytics Cookies</h3>

        <p>
            Analytics cookies help us understand how visitors use our website so we can
            improve performance, usability, and content.
        </p>

        <p>Information collected may include:</p>

        <ul>
            <li>Pages visited</li>
            <li>Time spent on pages</li>
            <li>Browser and device information</li>
            <li>Referring websites</li>
            <li>Website performance metrics</li>
        </ul>

        <p>
            We may use services such as Google Analytics to collect this information.
        </p>

        <h3>Advertising and Marketing Cookies</h3>

        <p>
            Advertising and marketing technologies help us measure the effectiveness of
            advertising campaigns and understand how visitors interact with our website
            after viewing advertisements.
        </p>

        <p>Information collected may include:</p>

        <ul>
            <li>Ad interactions</li>
            <li>Website visits</li>
            <li>Conversion events</li>
            <li>General device and browser information</li>
        </ul>

        <p>
            We may use services such as Meta Pixel for these purposes.
        </p>

        <h2>Managing Your Cookie Preferences</h2>

        <p>
            When required by applicable law, we will request your consent before placing
            analytics or advertising cookies on your device.
        </p>

        <p>
            You may update your cookie preferences at any time through our cookie
            preference tools when available.
        </p>

        <p>Most web browsers also allow you to:</p>

        <ul>
            <li>View stored cookies</li>
            <li>Delete cookies</li>
            <li>Block cookies</li>
            <li>Configure cookie notifications</li>
        </ul>

        <p>
            Disabling certain cookies may affect the functionality of the website.
        </p>

        <h2>Third-Party Technologies</h2>

        <p>
            Some cookies and similar technologies may be provided by third-party service
            providers that assist us with analytics, advertising, and website operations.
            These providers may collect information according to their own privacy
            policies.
        </p>

        <v-table
            class="mb-6"
        >
            <thead>
                <tr>
                    <th>Category</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Necessary</td>
                    <td>{{ brandTitle }}</td>
                    <td>Authentication, security, session management, consent preferences</td>
                </tr>
                <tr>
                    <td>Analytics</td>
                    <td>Google Analytics</td>
                    <td>Website analytics and performance measurement</td>
                </tr>
                <tr>
                    <td>Advertising &amp; Marketing</td>
                    <td>Meta Pixel</td>
                    <td>Advertising attribution and conversion measurement</td>
                </tr>
            </tbody>
        </v-table>

        <h2>Changes to This Policy</h2>

        <p>
            We may update this Cookie Policy from time to time. Any changes will be posted
            on this page with an updated effective date.
        </p>

        <h2>Contact Us</h2>

        <p>
            If you have questions about this Cookie Policy or our use of cookies, please
            contact us through the contact information provided on our website.
        </p>
    </ContainerContent>
</template>

<script lang="ts" setup>
    import ContainerContent from '~/components/ContainerContent/ContainerContent.vue';

    definePageMeta({
        scrollToTop: true
    });

    const $config = useRuntimeConfig();
    const brandTitle = $config.public.brand.title;

    useHead({
        title: `Cookie Policy | ${brandTitle}`,
        meta: [
            {
                name: 'description',
                content: 'Cookie Policy'
            }
        ]
    });
</script>
